import React, { useState } from "react";
import GroupCard from "@/components/GroupCard";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, ApiResponse } from "@/types/Api";
import { CreateGroup, ListGroup, UpdateGroup } from "@/types/Groups";
import GenericApi from "@/api/genericApi";
import { ApiRoutes } from "@/config/routes/ApiRoutes";
import { GroupQueryKey, UsersQueryKey } from "@/config/constants/QueryKeys";
import { Loader } from "@/components";
import { ValidationErrors } from "@/types/ValidationError";
import CustomLogger from "@/utils/CustomLogger";
import { Staff } from "@/types/User";
import { isBitramp } from "@/utils/utils";

const Groups: React.FC = () => {
  const queryClient = useQueryClient();
  const [editId, setEditId] = useState<number>(-1);

  const {
    data: groups,
    isLoading,
    error,
  } = useQuery<ApiResponse<ListGroup[]>>({
    queryFn: async () => GenericApi.get<ListGroup[]>(ApiRoutes.GROUP),
    queryKey: [GroupQueryKey],
  });

  const {
    data: users,
    isLoading: isLoadingUsers,
    error: errorUsers,
  } = useQuery<ApiResponse<Staff[]>>({
    queryFn: async () => GenericApi.get<Staff[]>(ApiRoutes.STAFF),
    queryKey: [UsersQueryKey],
  });

  const createGroupMutation = useMutation<
    ApiResponse<CreateGroup>,
    ApiError<ValidationErrors>,
    CreateGroup
  >({
    mutationFn: (data) =>
      GenericApi.post<CreateGroup>(ApiRoutes.GROUP, {
        ...data,
      }),
    onSuccess: async ({ data }) => {
      CustomLogger.log(data);
      queryClient.invalidateQueries({ queryKey: [GroupQueryKey] });
      setIsCreateModalOpen(false);
    },
    onError: async ({ error }) => {
      CustomLogger.error(error);
    },
  });
  const deleteGroupMutation = useMutation<
    ApiResponse<ListGroup>,
    ApiError<ValidationErrors>,
    { id: number }
  >({
    mutationFn: (data) =>
      GenericApi.delete<ListGroup>(`${ApiRoutes.GROUP}/${data.id}`),
    onSuccess: async ({ data }) => {
      CustomLogger.log(data);
      queryClient.invalidateQueries({ queryKey: [GroupQueryKey] });
    },
    onError: async ({ error }) => {
      CustomLogger.error(error);
    },
  });

  const editGroupMutation = useMutation<
    ApiResponse<UpdateGroup>,
    ApiError<ValidationErrors>,
    UpdateGroup
  >({
    mutationFn: (data) =>
      GenericApi.put<UpdateGroup>(`${ApiRoutes.GROUP}/${data.id}`, {
        ...data,
      }),
    onSuccess: async ({ data }) => {
      CustomLogger.log(data);
      queryClient.invalidateQueries({ queryKey: [GroupQueryKey] });
      setIsEditModeOpen(false);
    },
    onError: async ({ error }) => {
      CustomLogger.error(error);
    },
  });

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModeOpen, setIsEditModeOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");

  const handleCreateGroup = () => {
    createGroupMutation.mutate({
      name: newGroupName,
      permissions: [],
      users: [],
    });
  };

  const handleDeleteGroup = (groupId: number) => {
    deleteGroupMutation.mutate({
      id: groupId,
    });
  };

  const handleEditGroup = () => {
    if (editId === -1) {
      return;
    }

    editGroupMutation.mutate({
      id: editId,
      name: newGroupName,
      permissions: [],
      users: [],
    });
  };

  if (isLoading || isLoadingUsers) {
    return <Loader />;
  }

  if (error || errorUsers) {
    return <div>Error: {error?.message || errorUsers?.message}</div>;
  }

  return (
    <div className="groupsContainer Container pshadow">
      <div className="w-full flex justify-end items-center">
        <div
          className={`${
            isBitramp() ? "Create_button_bitramp" : "Outline_button"
          }`}
          onClick={() => setIsCreateModalOpen(true)}
        >
          Create new group <span className="text-[1.4375rem]">+</span>
        </div>
      </div>

      {/* <div className="groupsContainer_instructions">
        <div>Name</div>
        <span>Permissions</span>
        <div className="flex justify-end">Remove group</div>
      </div> */}

      <div className="groupsContainer_list">
        {groups?.data?.map((group, index) => (
          <GroupCard
            key={index}
            name={group.name}
            group={group}
            users={users?.data ?? []}
            isEditing={editId === group.id}
            onDelete={() => handleDeleteGroup(group.id)}
            onEdit={(id: number) => {
              setEditId(id);
            }}
          />
        ))}
      </div>

      {/* Modal pentru a introduce numele grupului */}
      {isCreateModalOpen && (
        <div className="modal">
          <div className="Container modal_content">
            <div className="Title bold dark-blue">Create New Group</div>
            <input
              type="text"
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              placeholder="Enter group name"
              className="modal_content__input Input"
            />
            <div className="flex gap-5">
              {" "}
              <button
                className={`${
                  isBitramp() ? "Upload_button_bitramp" : "Primary_button "
                }`}
                onClick={handleCreateGroup}
              >
                Create
              </button>
              <button
                className={` ${
                  isBitramp() ? "Cancel_button_bitramp" : "Outline_red_button"
                }`}
                onClick={() => setIsCreateModalOpen(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {isEditModeOpen && (
        <div className="modal">
          <div className="Container modal_content">
            <div className="Title bold dark-blue">Edit Group</div>
            <input
              type="text"
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              placeholder="Enter group name"
              className="modal_content__input Input"
            />
            <div className="flex gap-5">
              {" "}
              <button
                className={`Primary_button ${
                  isBitramp() ? "!bg-[#8AFEFD] !text-[#212121]" : ""
                }`}
                onClick={() => {
                  handleEditGroup();
                }}
              >
                Edit
              </button>
              <button
                className={`Outline_red_button ${
                  isBitramp()
                    ? "!bg-transparent !border-none !text-[#EE33D9]"
                    : ""
                }`}
                onClick={() => setIsEditModeOpen(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Groups;
